import { App } from "../../../../../shared/frontend/ui";
import type { FormInstance } from "../../../../../shared/frontend/ui";
import { Button, Form, Input, Space, Typography } from "../../../../../shared/frontend/ui";
import { DatePicker } from "../../../../../shared/frontend/components/ControlledPickers";
import dayjs from "dayjs";
import React from "react";
import { useBankAutofillFieldProps } from "../app/utils/browserAutofill";
import { getBankDataFromIban } from "../app/utils/ibanLookup";
import { DEFAULT_MANDATE_REFERENCE_PATTERN,generateMandateReference as buildMandateReference } from "../app/utils/mandateReference";
import { defaultBankAccountHolder,isValidBic,isValidIban,normalizeBic,normalizeIban } from "../app/utils/sepa";
import { ResponsiveFormGrid } from "./ui/antd";

const GERMAN_COUNTRY_CODE = "DE";
const COUNTRY_CODE_LENGTH = 2;

type ApiClient = {
  get: <T>(url: string) => Promise<T>;
};

type SepaBankDetailsFieldsProps = {
  api: ApiClient;
  autoGenerateMandateOnIban?: boolean;
  dateInputFormat: string | string[];
  defaultMandateSignedDate?: unknown | null;
  firstName?: string | null;
  form: FormInstance;
  homeStudioId?: string | null;
  initialIban?: string | null;
  lastName?: string | null;
  modal?: { confirm: ReturnType<typeof App.useApp>["modal"]["confirm"] };
  personId?: string | null;
  required?: boolean;
  setBankName?: (value: string | null) => void;
  showTitle?: boolean;
  studios?: Array<{ id: string; code?: string | null }>;
  text: (key: string) => string;
};

function parseMandateDate(value: unknown) {
  if (value == null) return null;
  if (dayjs.isDayjs(value)) return value;
  if (value instanceof Date || typeof value === "string" || typeof value === "number") return dayjs(value);
  return null;
}

export function SepaBankDetailsFields({
  api,
  autoGenerateMandateOnIban = true,
  dateInputFormat,
  defaultMandateSignedDate,
  firstName,
  form,
  homeStudioId,
  initialIban,
  lastName,
  modal,
  personId,
  required = false,
  setBankName,
  showTitle = true,
  studios,
  text
}: SepaBankDetailsFieldsProps) {
  const { inputProps: bankInputProps, labelHtmlFor: bankLabelHtmlFor } = useBankAutofillFieldProps();
  const [mandatePattern, setMandatePattern] = React.useState(DEFAULT_MANDATE_REFERENCE_PATTERN);
  const lastGeneratedMandateIbanRef = React.useRef<string | null>(null);
  const watchedIban = Form.useWatch("iban", form);
  const lastResolvedIbanRef = React.useRef<string | null>(null);
  const bankLookupRequestRef = React.useRef(0);

  React.useEffect(() => {
    api
      .get<{ pattern?: string | null }>("/api/v1/settings/mandate-reference-settings")
      .then((value) => setMandatePattern(String(value?.pattern || "").trim() || DEFAULT_MANDATE_REFERENCE_PATTERN))
      .catch(() => setMandatePattern(DEFAULT_MANDATE_REFERENCE_PATTERN));
  }, [api]);

  const generateMandateReference = React.useCallback(() => {
    const today = dayjs().format("YYYYMMDD");
    const studioCode = homeStudioId ? (studios || []).find((studio) => String(studio.id) === String(homeStudioId))?.code ?? "" : "";
    const mandateSignedDate = parseMandateDate(defaultMandateSignedDate);
    const reference = buildMandateReference({
      pattern: mandatePattern || DEFAULT_MANDATE_REFERENCE_PATTERN,
      personId,
      firstName,
      lastName,
      dateText: today,
      studioCode
    });
    form.setFieldsValue({ mandateReference: reference, mandateSignedDate: mandateSignedDate?.isValid() ? mandateSignedDate : dayjs() });
  }, [defaultMandateSignedDate, firstName, form, homeStudioId, lastName, mandatePattern, personId, studios]);

  const generateMandateReferenceWithConfirm = React.useCallback(() => {
    const existing = String(form.getFieldValue("mandateReference") || "").trim();
    if (!existing || !modal) {
      generateMandateReference();
      return;
    }
    modal.confirm({
      title: text("Regenerate mandate reference?"),
      content: text("Mandate reference exists confirm"),
      okText: text("Yes, regenerate"),
      cancelText: text("Cancel"),
      okButtonProps: { danger: true },
      onOk: async () => generateMandateReference()
    });
  }, [form, generateMandateReference, modal, text]);

  const fillBankDataFromIban = React.useCallback(
    async (iban: string, options?: { clearOnFailure?: boolean }) => {
      const normalizedIban = normalizeIban(iban);
      if (!normalizedIban || !isValidIban(normalizedIban)) {
        bankLookupRequestRef.current += 1;
        lastResolvedIbanRef.current = null;
        if (options?.clearOnFailure) {
          form.setFieldValue("bankName", "");
          setBankName?.(null);
        }
        return;
      }
      if (lastResolvedIbanRef.current === normalizedIban) return;

      const requestId = ++bankLookupRequestRef.current;
      try {
        const bankData = await getBankDataFromIban(normalizedIban, api);
        if (requestId !== bankLookupRequestRef.current) return;
        if (normalizeIban(String(form.getFieldValue("iban") || "")) !== normalizedIban) return;
        lastResolvedIbanRef.current = normalizedIban;
        if (!bankData) return;
        if (!form.getFieldValue("bic") && bankData.bic) form.setFieldValue("bic", bankData.bic);
        form.setFieldValue("bankName", bankData.bankName || "");
        setBankName?.(bankData.bankName || null);
      } catch {
        if (requestId !== bankLookupRequestRef.current) return;
        lastResolvedIbanRef.current = null;
        if (options?.clearOnFailure) {
          form.setFieldValue("bankName", "");
          setBankName?.(null);
        }
      }
    },
    [api, form, setBankName]
  );

  React.useEffect(() => {
    void fillBankDataFromIban(String(watchedIban || ""));
  }, [fillBankDataFromIban, watchedIban]);

  React.useEffect(() => {
    if (String(form.getFieldValue("bankAccountHolder") || "").trim()) return;
    const holder = defaultBankAccountHolder(firstName, lastName);
    if (holder) form.setFieldValue("bankAccountHolder", holder);
  }, [firstName, form, lastName]);

  return (
    <>
      {showTitle ? <Typography.Text strong>{text("Bank details")}</Typography.Text> : null}
      <ResponsiveFormGrid columns={3}>
        <Form.Item name="bankAccountHolder" label={text("Account holder")} htmlFor={bankLabelHtmlFor.accountHolder}>
          <Input {...bankInputProps.accountHolder} />
        </Form.Item>
        <Form.Item
          name="iban"
          label={text("IBAN")}
          htmlFor={bankLabelHtmlFor.iban}
          rules={[
            {
              validator: async (_, value) => {
                const iban = normalizeIban(String(value || ""));
                if (!iban) return;
                if (!isValidIban(iban)) throw new Error(text("Invalid IBAN"));
              }
            }
          ]}
        >
          <Input
            placeholder={text("e.g. DE89370400440532013000")}
            {...bankInputProps.iban}
            onBlur={async (event) => {
              const raw = event.target.value ?? "";
              const iban = normalizeIban(raw);
              if (iban && iban !== raw) form.setFieldValue("iban", iban);
              if (!iban || !isValidIban(iban)) {
                await fillBankDataFromIban(iban, { clearOnFailure: true });
                return;
              }
              await fillBankDataFromIban(iban, { clearOnFailure: true });
              const initialNormalizedIban = normalizeIban(initialIban || "");
              const hasExistingMandateReference = Boolean(String(form.getFieldValue("mandateReference") || "").trim());
              const shouldGenerateMandate =
                autoGenerateMandateOnIban &&
                (!hasExistingMandateReference || (initialNormalizedIban && iban !== initialNormalizedIban));
              if (shouldGenerateMandate && lastGeneratedMandateIbanRef.current !== iban) {
                generateMandateReference();
                lastGeneratedMandateIbanRef.current = iban;
              }
            }}
          />
        </Form.Item>
        <Form.Item
          name="bic"
          label={text("BIC")}
          htmlFor={bankLabelHtmlFor.bic}
          dependencies={["iban"]}
          rules={[
            {
              validator: async (_, value) => {
                const iban = normalizeIban(String(form.getFieldValue("iban") || ""));
                const bic = normalizeBic(String(value || ""));
                if (iban && iban.substring(0, COUNTRY_CODE_LENGTH) !== GERMAN_COUNTRY_CODE && !bic) {
                  throw new Error(text("BIC is required for foreign IBANs"));
                }
                if (bic && !isValidBic(bic)) throw new Error(text("Invalid BIC"));
              }
            }
          ]}
        >
          <Input
            placeholder={text("e.g. COBADEFFXXX")}
            {...bankInputProps.bic}
            onBlur={(event) => {
              const raw = event.target.value ?? "";
              const bic = normalizeBic(raw);
              if (bic && bic !== raw) form.setFieldValue("bic", bic);
            }}
          />
        </Form.Item>
      </ResponsiveFormGrid>
      <ResponsiveFormGrid columns={3}>
        <Form.Item name="bankName" label={text("Bank")} htmlFor={bankLabelHtmlFor.bankName}>
          <Input placeholder={text("Bank placeholder example")} {...bankInputProps.bankName} />
        </Form.Item>
        <Form.Item label={text("Mandate reference")} htmlFor={bankLabelHtmlFor.mandateReference} required={required}>
          <Space.Compact style={{ width: "100%" }}>
            <Form.Item name="mandateReference" noStyle rules={required ? [{ required: true, message: text("Mandate reference is required") }] : []}>
              <Input readOnly {...bankInputProps.mandateReference} />
            </Form.Item>
            <Button onClick={generateMandateReferenceWithConfirm}>{text("New")}</Button>
          </Space.Compact>
        </Form.Item>
        <Form.Item name="mandateSignedDate" label={text("Mandate signed on")} rules={required ? [{ required: true, message: text("Mandate date is required") }] : []}>
          <DatePicker allowClear placeholder={text("—")} format={dateInputFormat} style={{ width: "100%" }} />
        </Form.Item>
      </ResponsiveFormGrid>
    </>
  );
}
